import { useState } from 'react';
import { useSelector } from 'react-redux';
import fm from 'format-message';
import PropTypes from 'prop-types';
import ScrollableBox from '@activebrands/core-web/components/ScrollableBox';
import ErrorNotification from '@activebrands/core-web/components/notifications/ErrorNotification';
import useNotifications from '@activebrands/core-web/hooks/useNotifications';
import { styled } from '@activebrands/core-web/libs/styletron';
import {
    ADD_VOUCHER_COMBINATION_ERROR,
    addActVoucher,
    removeVoucher,
} from '@activebrands/core-web/state/basket/actions';
import { useTypedDispatch } from '@activebrands/core-web/state/store';
import { logger } from '@activebrands/core-web/utils/logger';
import Paragraph from 'components/text/Paragraph';
import ActBonusVoucher from './ActBonusVoucher';

const ACT_REWARDS_PRICELIST = process.env.REACT_APP_ACT_REWARDS_PRICELIST;

const BonusVouchersWrapper = styled('div', {});

const ActBonusVouchers = ({ isVouchersAvailable }) => {
    const dispatch = useTypedDispatch();
    const activeVouchers = useSelector(state => state.basket?.totalDiscount?.discounts) || [];
    const bonusChecks = useSelector(state => state.auth?.user?.extendedProperties?.voyadoPromotions) || [];
    const { user } = useSelector(state => state.auth);
    const [loadingVouchersIds, setLoadingVouchersIds] = useState([]);
    const notifications = useNotifications();
    const pricelistId = useSelector(state => state.application.shopConfig.pricelistId);

    if (bonusChecks.length < 1) {
        return null;
    }

    const actRewardsPricelist = ACT_REWARDS_PRICELIST === pricelistId;

    const activeVouchersIds = activeVouchers.map(voucher => voucher.id);
    const bonusCheckIsActive = id => activeVouchersIds.includes(id);
    const bonusCheckIsLoading = id => loadingVouchersIds.includes(id);

    const handleBonusCheckClick = async bonusCheck => {
        const remove = bonusCheckIsActive(bonusCheck.id);
        setLoadingVouchersIds(prevState => [...prevState, bonusCheck.id]);

        try {
            if (remove) {
                await dispatch(removeVoucher(bonusCheck.id));
            } else {
                await dispatch(addActVoucher(bonusCheck.id, user?.token));
            }
        } catch (e) {
            logger.error(e);

            if (remove) {
                // error handlig for remove is handled in basket-state
            } else {
                if (ADD_VOUCHER_COMBINATION_ERROR) {
                    notifications.push(ErrorNotification, {
                        message: `${fm('Something went wrong...')} ${fm('You can not combine these vouchers.')}`,
                    });
                } else {
                    notifications.push(ErrorNotification, {
                        message: `${fm('Something went wrong...')} ${fm('We were not able to add that voucher.')}`,
                    });
                }
            }
        }
        // Reset loading
        setLoadingVouchersIds(prevState => prevState.filter(id => id !== bonusCheck.id));
    };

    // Hide redeemed vouchers
    const notRedeemedVouchers = bonusChecks?.filter(bonusCheck => !bonusCheck.redeemed);

    return (
        <BonusVouchersWrapper>
            {bonusChecks.length > 0 && actRewardsPricelist ? (
                isVouchersAvailable ? (
                    <Paragraph as="span" fontKeys="Misc/12_100_-015">
                        {fm('Available bonus vouchers')}
                    </Paragraph>
                ) : (
                    <Paragraph as="span" fontKeys="Misc/12_100_-015">
                        {fm('Vouchers are only available with Norway selected')}
                    </Paragraph>
                )
            ) : (
                <Paragraph as="span" fontKeys="Misc/12_100_-015">
                    {fm('No vouchers available')}
                </Paragraph>
            )}
            {actRewardsPricelist && isVouchersAvailable && (
                <ScrollableBox
                    $style={{ display: 'flex', gap: '4px', marginTop: '12px' }}
                    as="ul"
                    scrolldirection="horizontal"
                >
                    {notRedeemedVouchers.map(bonusCheck => (
                        <ActBonusVoucher
                            active={bonusCheckIsActive(bonusCheck.id)}
                            data={bonusCheck}
                            handleClick={handleBonusCheckClick}
                            key={bonusCheck.id}
                            loading={bonusCheckIsLoading(bonusCheck.id)}
                        />
                    ))}
                </ScrollableBox>
            )}
        </BonusVouchersWrapper>
    );
};

ActBonusVouchers.propTypes = {
    isVouchersAvailable: PropTypes.bool,
};

export default ActBonusVouchers;
